import { selectors } from '@/app/store';
import { useAppSelector } from '@/app/store/hooks';
import { COLORS, TYPO } from '@/b2b/shared/consts';
import { useClientSize } from '@/b2b/shared/hooks';
import { ArrowUp16 } from '@/b2b/shared/icons';
import { BreakpointsEnum } from '@/b2b/shared/types';
import { UiFlex, UiGi, UiGrid } from '@/b2b/shared/ui';
import { FC, Fragment } from 'react';
import styled from 'styled-components';
import { footerContent } from '../model/footer.consts';
import { LinksColumn } from './linksColumn';
import { LoginBlock } from './loginBlock';
import { ServiceBlock } from './serviceBlock';
import { SocialBlock } from './socialBlock';
import { SubscriptionBlock } from './subscriptionBlock';

const Footer: FC = () => {
  const { getIsBreakpoint } = useClientSize();
  const isMobile = getIsBreakpoint('lgm');

  const metadata = useAppSelector(selectors.auth.selectMetadata);
  const isB2B = metadata?.isB2B ?? false;

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <Root>
      {isMobile ? (
        <Container
          direction="column"
          gap={32}
        >
          <LoginBlock isB2B={isB2B} />
          <ServiceBlock
            buttonPosition="top"
            emailToSupport={footerContent.emailToSupport}
            linkToSupport={footerContent.linkToSupport}
          />
          <Line />
          <LinksColumn
            links={footerContent.linksForClients}
            title="Для клиентов"
          />
          <LinksColumn
            links={footerContent.linksForTherapists}
            title="Для психологов"
          />
          <LinksColumn
            links={footerContent.linksForBusiness}
            title="Для бизнеса"
          />
          <LinksColumn
            links={footerContent.contacts}
            title="Контакты компании"
            slotPosition="top"
          />
          <Line />
          <SubscriptionBlock />
          <SocialBlock socialLinks={footerContent.socialLinks} />
        </Container>
      ) : (
        <Container
          direction="column"
          gap={48}
        >
          <UiGrid
            xGap={40}
            cols={4}
          >
            <UiGi>
              <LoginBlock isB2B={isB2B} />
            </UiGi>
            <UiGi>
              <LinksColumn
                links={footerContent.linksForClients}
                title="Для клиентов"
              />
            </UiGi>
            <UiGi>
              <LinksColumn
                links={footerContent.linksForTherapists}
                title="Для психологов"
              />
            </UiGi>
            <UiGi>
              <LinksColumn
                links={footerContent.linksForBusiness}
                title="Для бизнеса"
              />
            </UiGi>
          </UiGrid>

          <Line />

          <UiGrid
            xGap={40}
            cols={4}
          >
            <UiGi>
              <ServiceBlock
                buttonPosition="top"
                emailToSupport={footerContent.emailToSupport}
                linkToSupport={footerContent.linkToSupport}
              />
            </UiGi>
            <UiGi>
              <LinksColumn
                links={footerContent.contacts}
                title="Контакты компании"
                slotPosition="top"
              />
            </UiGi>
            <UiGi>
              <SocialBlock socialLinks={footerContent.socialLinks} />
            </UiGi>
            <UiGi>
              <SubscriptionBlock />
            </UiGi>
          </UiGrid>
        </Container>
      )}
      <BottomWrapper
        align="center"
        justify="center"
      >
        <Bottom
          align="center"
          justify="space-between"
          gap={isMobile ? 32 : 4}
          direction={isMobile ? 'column' : 'row'}
        >
          <BottomContent
            align="center"
            justify="start"
            gap={isMobile ? 10 : 4}
            direction={isMobile ? 'column' : 'row'}
          >
            <p>
              © {new Date().getFullYear()} {footerContent.copyright}
            </p>
            {footerContent.legalLinks.map((link) => (
              <Fragment key={link.text}>
                {isMobile ? '' : '•'}
                <a
                  href={link.url}
                  target={link.target}
                >
                  {link.text}
                </a>
              </Fragment>
            ))}
          </BottomContent>
          <ScrollToTop
            align="center"
            justify="center"
            gap={4}
            onClick={scrollToTop}
          >
            <ArrowUp16 /> Вернуться наверх
          </ScrollToTop>
        </Bottom>
      </BottomWrapper>
    </Root>
  );
};

export default Footer;

const Root = styled.footer`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 100vw;
  background-color: ${COLORS.white};
  margin: 0;
  border-radius: 0;
  overflow: hidden;

  @media (min-width: ${BreakpointsEnum.lgm}px) {
    max-width: calc(100vw - 28px);
    margin: 0 12px 12px;
    border-radius: 40px;
  }
`;

const Container = styled(UiFlex)`
  width: 100%;
  max-width: 1280px;
  padding: 40px 32px;

  @media (min-width: ${BreakpointsEnum.lgm}px) {
    padding: 60px 0;
  }
`;

const BottomWrapper = styled(UiFlex)`
  width: 100%;
  height: 172px;
  background-color: ${COLORS.black50};

  @media (min-width: ${BreakpointsEnum.lgm}px) {
    height: 56px;
  }
`;

const Bottom = styled(UiFlex)`
  width: 100%;
  max-width: 1280px;
  color: ${COLORS.caption};
`;

const BottomContent = styled(UiFlex)`
  width: 100%;
  max-width: 1280px;
`;

const ScrollToTop = styled(UiFlex)`
  ${TYPO.caption1}
  width: 165px;
  cursor: pointer;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${COLORS.grayscale100};

  @media (min-width: ${BreakpointsEnum.lgm}px) {
    background-color: ${COLORS.white};
  }
`;
